/**
 * Thin axios client for the app-api internal review endpoints consumed by the
 * admin portal (staff-only).
 *
 * Mirrors the shape of src/api/erp-integration-client.ts:
 *   - module-scoped memoized axios instance
 *   - baseURL + internal-auth Bearer token injected via `configureClient`
 *
 * The backend routes are declared `x-internal: true` in app-api's openapi.yml
 * (hidden from docs and generated clients) and guarded server-side by an
 * internal-auth issuer check — only admin-portal internal-auth tokens pass.
 *
 * The review table is keyed by the app owner's org, so approve/reject take an
 * explicit `orgId` which is sent as the `org_id` query parameter. The list
 * response includes `org_id` per entry for exactly this purpose.
 *
 * Types are kept in sync with app-api openapi.yml (schema: InternalReview).
 */
import axios, { AxiosInstance } from 'axios'

import { config } from '../configs/api.config'

import { configureClient } from './api-utils'

// --- types ------------------------------------------------------------------

export type AppReviewStatus = 'pending' | 'approved' | 'rejected'

export interface AppReview {
  app_id: string
  org_id: string
  version: string
  review_status: AppReviewStatus
  requested_at?: string
  requested_by?: string
  reviewed_at?: string
  reviewed_by?: string
  technical_contact?: string
  marketing_contact?: string
  demo_url?: string
}

// --- memoized axios factory -------------------------------------------------

let client: AxiosInstance | undefined

const getAxios = (): AxiosInstance => {
  if (!client) {
    client = axios.create({
      baseURL: config.APP
    })

    configureClient(client, {
      baseURL: config.APP
    })
  }

  return client
}

// --- public API -------------------------------------------------------------

export const getAppClient = () => {
  const c = getAxios()

  return {
    /** Lists reviews of all statuses when `status` is omitted. */
    listReviews: async (status?: AppReviewStatus): Promise<AppReview[]> => {
      const response = await c.get<{ results: AppReview[] }>(
        '/v1/internal/reviews',
        { params: status ? { status } : {} }
      )

      return response.data.results ?? []
    },

    approveReview: async (params: {
      appId: string
      version: string
      orgId: string
    }): Promise<void> => {
      const encodedAppId = encodeURIComponent(params.appId)
      const encodedVersion = encodeURIComponent(params.version)

      await c.post(
        `/v1/internal/app-configurations/${encodedAppId}/versions/${encodedVersion}/review:approve`,
        undefined,
        { params: { org_id: params.orgId } }
      )
    },

    rejectReview: async (params: {
      appId: string
      version: string
      orgId: string
    }): Promise<void> => {
      const encodedAppId = encodeURIComponent(params.appId)
      const encodedVersion = encodeURIComponent(params.version)

      await c.post(
        `/v1/internal/app-configurations/${encodedAppId}/versions/${encodedVersion}/review:reject`,
        undefined,
        { params: { org_id: params.orgId } }
      )
    }
  }
}
